import React, { useEffect, useMemo, useState } from "react";
import { Container, ErrorMessage, SinglePageable, SinglePageableResponse, } from "../../../core/component";
import { Button, Dropdown, Empty, Form, message } from "antd";
import { confirm, notify, translate } from "../../../core";
import {
  CloseOutlined,
  DeleteOutlined,
  HistoryOutlined,
  PlusOutlined,
  PrinterOutlined,
  SaveFilled,
  SearchOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { BadRequestException, EntryMode, Nullable, PageableRequest, PageContextValue, } from "../../../core/type";
import { Order, OrderAddress, OrderEntry, OrderFindRequest } from "../type";
import { OrderForm } from "./OrderForm";
import { OrderSearch } from "./OrderSearch";
import styles from "./Order.module.scss";
import { OrderPageContext } from "./OrderPageContext";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { EnvelopIcon } from "../../../component/icon";
import { useOrderData } from "../hook/order-data.hook";
import { useNavigate, useParams } from "react-router-dom";
import { GetOrderPage, GetOrderPrint, GetOrders, SendOrderByEmail, } from "../graphql/order-queries.graphql";
import ContactHistory from "../../contact/component/ContactHistory";
import { client } from "../../../core/query";
import { ReportPdfViewer, ReportPdfViewerProps, } from "../../../core/component/Report";
import OrderPrint from "./OrderPrint";
import { OrderMailLabelButton } from "./OrderMailLabelButton";
import { SendEmail } from "../../../core/component/SendEmail";
import { AddressType } from "../../contact/type/contact";
import { dateDisplay } from "../../../core/until";

type PdfProps = Pick<ReportPdfViewerProps, "name" | "title" | "params">;

export const OrderPage = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [isRefetch, setIsRefetch] = useState<boolean>(false);
  const [entity, setEntity] = useState<OrderEntry>();
  const [page, setPage] = useState<number | null>();
  const [errors, setErrors] = useState<string[]>([]);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [form] = Form.useForm<OrderEntry>();
  const [find] = useLazyQuery(GetOrders);
  const [search, setSearch] = useState<OrderFindRequest>();
  const [entryMode, setEntryMode] = useState<EntryMode>(EntryMode.Nothing);
  const { init, toEntry, toDto, create, update, remove } = useOrderData();
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
  const [selectedReport, setSelectedReport] = useState<
    "OrderPrint" | "History"
  >();
  const [selectedCustomer, setSelectedCustomer] = useState<number>();
  const [sendOrderByEmail, { loading: sendingOrderByEmail }] = useMutation(
    SendOrderByEmail,
    {
      onCompleted: () => {
        setOpenSendEmail(false);
        messageApi.success(translate("email", "sent"));
      },
      onError: (error) => messageApi.error(error.message),
    }
  );
  const [messageApi, contextHolder] = message.useMessage();
  const [pdfProps, setPdfProps] = useState<Nullable<PdfProps>>(null);
  const { number } = useParams<{ number?: string }>();
  const [openSendEmail, setOpenSendEmail] = useState<boolean>(false);
  const navigate = useNavigate();
  const [data, setData] = useState<Order | null>(null);
  const [shipping, setShipping] = useState<OrderAddress>();

  useQuery<{ orderPage: number }, { number: number }>(GetOrderPage, {
    variables: {
      number: Number(number),
    },
    skip: !number,
    onCompleted: (dataPage) => {
      if (data?.number != number) {
        fetch({}, dataPage.orderPage)
      }
    },
  });

  useEffect(() => {
    setIsFormChanged(false)
  }, [entity]);

  const add = async () => {
    try {
      setLoading(true);
      setEntryMode(EntryMode.New);
      setShipping(undefined);
      const entity = await init();
      setEntity(toEntry(entity));
      form.resetFields();
      form.setFieldsValue(toEntry(entity) as any);
    } finally {
      setLoading(false);
    }
  };

  const save = async (data: OrderEntry) => {
    setSending(true);
    setErrors([]);
    const dto = toDto(data);
    const id = entity?.id;

    try {
      if (id) await update(id, dto);
      else {
        const result = await create(dto);
        if (entity) entity.id = result.id;
      }

      notify.success(translate("order"), translate("save_success_message"));
      setEntryMode(EntryMode.Nothing);
      setIsFormChanged(false);

      await client.refetchQueries({
        include: [GetOrderPrint],
      });

      await refresh()
    } catch (e) {
      if (e instanceof BadRequestException) setErrors(e.message);
    } finally {
      setSending(false);
    }
  };

  const handleRemove = async () => {
    if (!entity) return;

    const result = await confirm(
      translate("remove_confirmation_message", "order", "?")
    );

    if (!result) return;

    try {
      await remove(entity.id);

      notify.success(
        translate("remove", "order"),
        translate("the", "order", "remove_success_message")
      );

      setEntity(undefined);
    } catch (e) {
      if (e instanceof BadRequestException)
        notify.error(
          translate("remove", "order"),
          e.message,
        )
    }
  };

  const onChange = async (
    request: PageableRequest,
    page: number
  ): Promise<SinglePageableResponse<Order>> => {
    setLoading(true);

    try {
      const { data } = await find({
        variables: {
          request: {
            ...search,
            take: request.take || 1,
            skip: request.skip || 0,
          },
        },
      });

      const [entity] = data?.ordersFind.data ?? [];
      const shipping = entity?.addresses.find(ad => ad?.type === AddressType.Shipping) as OrderAddress;
      setShipping(shipping);
      setData(entity);
      navigate(`/order/${entity?.number}`);
      setErrors([]);
      const entry = entity && toEntry(entity);
      setEntity(entry);
      form.setFieldsValue(entry as any);
      setEntryMode(EntryMode.Modify);

      if (!page) setPage(1);

      return { count: data?.ordersFind.count ?? 0 };
    } finally {
      setLoading(false);
    }
  };

  const fetch = (search: any, page?: number) => {
    setSearch(search);
    setPage(0);
    setTimeout(() => setPage(page), 500);
  };

  const refresh = async () => {
    const { data } = await find({
      variables: {
        request: {
          id: entity?.id ?? 0,
          take: 1,
          skip: 0,
        },
      },
    });

    const [row] = data?.ordersFind.data ?? [];
    const entry = toEntry(row);
    setEntity(entry);
    setEntryMode(EntryMode.Modify)
    form.setFieldsValue(entry)
  };

  const printDocument = () => {
    window.print();
  };

  const contextValue = useMemo<PageContextValue<any, any>>(
    () => ({
      form,
      save,
      entity,
      refresh,
      fetch,
      isFormChanged,
      onFormChange: () => setIsFormChanged(true),
    }),
    [form, save, entity, refresh, fetch, isFormChanged]
  );

  return (
    <OrderPageContext.Provider value={contextValue}>
      <Container
        leftClassName={`action-container ${styles.toolbarButtons}`}
        paddingClass="0"
        loading={loading}
        toolbarItems={{
          start: [
            <div key="search-add-group" className="button-group">
              <Button
                onClick={() => setSearchOpen(true)}
                icon={<SearchOutlined />}
                className="btn-primary-gradient"
              >
                {translate("search")}
              </Button>

              <Button
                onClick={add}
                icon={<PlusOutlined />}
                className="btn-primary-gradient"
              >
                {translate("add")}
              </Button>
            </div>,

            <div key="save-remove-clear-group" className="button-group">
              <Button
                loading={sending}
                onClick={() => form.submit()}
                disabled={!entity}
                type={isFormChanged ? "primary" : "default"}
                icon={<SaveFilled />}
                className={isFormChanged ? "btn-primary-gradient" : ""}
              >
                {translate("save")}
              </Button>

              <Button
                danger
                className="danger-button"
                disabled={!entity?.id}
                onClick={handleRemove}
                icon={<DeleteOutlined />}
              >
                {translate("remove")}
              </Button>

              <Button
                disabled={entryMode === EntryMode.Nothing}
                onClick={async () => {
                  if (entryMode === EntryMode.New) await add();
                  if (entryMode === EntryMode.Modify) await refresh();
                }}
                icon={<StopOutlined />}
              >
                {translate("clear")}
              </Button>
            </div>,

            <div key="actions-group" className="button-group">
              <Button
                disabled={!entity?.id}
                loading={sendingOrderByEmail}
                onClick={() => setOpenSendEmail(true)}
                icon={<EnvelopIcon />}
              >
                {translate("email")}
              </Button>

              <Button
                icon={<HistoryOutlined />}
                disabled={!entity?.id}
                onClick={() => {
                  const customerId = form.getFieldValue(["customer", "id"]);

                  if (!customerId) return;

                  setPdfProps({
                    name: "sales-by-customer",
                    title: translate("sales", "by", "customer"),
                    params: {
                      customerId: customerId,
                    },
                  });
                }}
              >
                {translate("history")}
              </Button>

              <Button
                disabled={!entity?.id}
                onClick={() => {
                  window.print();
                }}
                icon={<PrinterOutlined />}
              >
                {translate("print")}
              </Button>

              <OrderMailLabelButton
                orderId={entity?.id}
                unsaved={isFormChanged}
              />
            </div>,

            <div key="exit-group" className="button-group">
              <Button
                disabled={!entity}
                onClick={() => {
                  setEntity(undefined);
                  setEntryMode(EntryMode.Nothing);
                }}
                icon={<CloseOutlined />}
              >
                {translate("exit")}
              </Button>
            </div>,
          ],
          navigation: [
            <SinglePageable
              key={"order-page-pagination"}
              onChange={onChange}
              defaultPage={page}
            />,
          ],
        }}
      >
        {contextHolder}
        <OrderSearch open={searchOpen} closeMe={() => setSearchOpen(false)} />

        {entity ? (
          <>
            <div
              className={[
                "print-hidden mt-2 bg-gray",
                styles["orderEntryForm"],
              ].join(" ")}
            >
              <ErrorMessage
                title={translate("entry_fail_message", "order")}
                message={errors}
              />
              <OrderForm shipping={shipping ?? undefined} />
            </div>

            <OrderPrint orderId={entity?.id} enabled={true} />

            {selectedCustomer && selectedReport === "History" ? (
              <ContactHistory
                id={selectedCustomer}
                isRefetch={isRefetch}
                fetched={() => setIsRefetch(false)}
              />
            ) : (
              ""
            )}
          </>
        ) : (
          <Empty className="mt-4" />
        )}
        <div className="loadFont">font</div>
      </Container>

      {entity && pdfProps ? (
        <ReportPdfViewer
          {...pdfProps}
          open={!!pdfProps}
          onCancel={() => setPdfProps(null)}
        />
      ) : (
        ""
      )}

      <SendEmail
        key={`send-email-${entity?.id}`}
        attachment={{
          enabled: true,
          prefix: "order-attachments",
        }}
        open={openSendEmail}
        onCancel={() => setOpenSendEmail(false)}
        onSend={(data) =>
          sendOrderByEmail({
            variables: {
              request: {
                id: entity?.id ?? 0,
                ...data,
              },
            },
          })
        }
        sending={sendingOrderByEmail}
        emailInputEnabled={{
          email: entity?.customerEmail,
          subject: `Order #${entity?.number} | Joseph Gann Jewelers`,
          text: `Dear ${entity?.customer?.firstName} ${entity?.customer?.lastName},

We deeply value your trust and look forward to serving you again.
Attached is your receipt for Order ${entity?.number}, placed on ${dateDisplay(entity?.date)}.

If you have any questions or need assistance, feel free to reply to this email or contact us via +1-617-426-4932.

Best regards,
Joseph Gann Jewelers
www.josephgann.com`,
        }}
      />
    </OrderPageContext.Provider>
  );
};
